/**
 * @module cercaliagl/view/VMeteoPopup
 */

import $ from 'jquery';
import BaseView from './BaseView';
import { i18n } from '../dictionary';
import { createSpanIconByNumPath } from './SVGUtils';

/**
 * @typedef {Object} ForecastInfo
 * @property {string} [localityId]
 * @property {string} [locality]
 * @property {Object.<string, string>} [day1]
 * @property {Object.<string, string>} [day2]
 * @property {Object.<string, string>} [day3]
 * @property {Object.<string, string>} [day4]
 * @property {Object.<string, string>} [day5]
 * @property {Object.<string, string>} [day6]
 */

/**
 * @enum {string}
 */
const SpanIcon = {
  F11: createSpanIconByNumPath('icon-11'),
  F12: createSpanIconByNumPath('icon-12', 2),
  F13: createSpanIconByNumPath('icon-13', 2),
  F14: createSpanIconByNumPath('icon-14', 2),
  F15: createSpanIconByNumPath('icon-15'),
  F16: createSpanIconByNumPath('icon-16'),
  F17: createSpanIconByNumPath('icon-17'),
  F18: createSpanIconByNumPath('icon-18'),
  F19: createSpanIconByNumPath('icon-19'),
  F20: createSpanIconByNumPath('icon-20'),
  F21: createSpanIconByNumPath('icon-21'),
  F22: createSpanIconByNumPath('icon-22'),
  F23: createSpanIconByNumPath('icon-23', 6),
  F24: createSpanIconByNumPath('icon-24', 9),
  F25: createSpanIconByNumPath('icon-25', 9),
  F26: createSpanIconByNumPath('icon-26', 7),
  F33: createSpanIconByNumPath('icon-33', 4),
  F34: createSpanIconByNumPath('icon-34', 5),
  F35: createSpanIconByNumPath('icon-35', 5),
  F36: createSpanIconByNumPath('icon-36', 4),
  F43: createSpanIconByNumPath('icon-43', 6),
  F44: createSpanIconByNumPath('icon-44', 6),
  F45: createSpanIconByNumPath('icon-45', 6),
  F46: createSpanIconByNumPath('icon-46', 5),
  F51: createSpanIconByNumPath('icon-51', 8),
  F52: createSpanIconByNumPath('icon-52', 7),
  F53: createSpanIconByNumPath('icon-53', 7),
  F54: createSpanIconByNumPath('icon-54', 7),
  F61: createSpanIconByNumPath('icon-61', 8),
  F62: createSpanIconByNumPath('icon-62', 7),
  F63: createSpanIconByNumPath('icon-63', 7),
  F64: createSpanIconByNumPath('icon-64', 7),
  F71: createSpanIconByNumPath('icon-71', 4),
  F72: createSpanIconByNumPath('icon-72', 5),
  F73: createSpanIconByNumPath('icon-73', 5),
  F74: createSpanIconByNumPath('icon-74', 4),
  V50: createSpanIconByNumPath('icon-v50'),
  V51: createSpanIconByNumPath('icon-v51'),
  V52: createSpanIconByNumPath('icon-v52'),
  V53: createSpanIconByNumPath('icon-v53'),
};

/**
 * @classdesc
 */
class VMeteoPopup extends BaseView {
  /**
   * @constructor
   */
  constructor() {
    const jqElement = $('<div class="v-meteopopup mdc-elevation--z4"></div>');
    super(jqElement);
    /**
     * @private
     * @type {ForecastInfo}
     */
    this.forecastData_ = {};
  }

  /**
   * @private
   * @param {string} indexDay Index day
   * @return {string} HTML string
   */
  renderNoDetailedDays_(indexDay) {
    return `
      <div class="${indexDay} day">
        <div class="date mini-width">${this.forecastData_[indexDay].date}</div>
        <div class="summary">
          ${this.forecastData_[indexDay].fullday}
          <div class="temp-min">${this.forecastData_[indexDay].tempMin}º</div>
          <div class="temp-max">${this.forecastData_[indexDay].tempMax}º</div>
          ${this.forecastData_[indexDay].rain ? `<div class="rain">${this.forecastData_[indexDay].rain}%</div>` : ''}
        </div>
      </div>
    `;
  }

  /**
   * @private
   * @param {string} indexDay Index day
   * @return {string} HTML string
   */
  renderDetailedDays_(indexDay) {
    return `
      <div class="${indexDay} day">
        <div class="date">${this.forecastData_[indexDay].date}</div>
        <div class="detail-day">
          <div class="morn-aft-temp">
            <span>${i18n('morning')}</span>
            ${this.forecastData_[indexDay].morning}
          </div>
          <div class="morn-aft-temp">
            <span>${i18n('afternoon')}</span>
            ${this.forecastData_[indexDay].afternoon}
          </div>
          <div class="morn-aft-temp last">
            <div class="temp-wind-rain">
              <div class="temp">
                <div class="temp-min">${this.forecastData_[indexDay].tempMin}º</div>
                <div class="temp-max">${this.forecastData_[indexDay].tempMax}º</div>
              </div>
              <div class="wind">
                ${this.forecastData_[indexDay].wind}
                ${this.forecastData_[indexDay].rain ? `<span class="rain">${this.forecastData_[indexDay].rain}%</span>` : ''}
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * @private
   */
  render_() {
    const html = `
    <div class="header">
      <span>${this.forecastData_.locality}</span>
    </div>
    <div class="met-content">
      ${this.renderDetailedDays_('day1')}
      ${this.renderDetailedDays_('day2')}
      ${this.renderNoDetailedDays_('day3')}
      ${this.renderNoDetailedDays_('day4')}
      ${this.renderNoDetailedDays_('day5')}
      ${this.renderNoDetailedDays_('day6')}
    </div>
      <div class="actions">
        <button type="button" class="mdc-button ok"><span class="mdc-button__ripple"></span>OK</button>
      </div>
    `;
    this.jqElement_
      .empty()
      .append(html);

    this.show();
    this.jqElement_.find('button.ok').click(() => this.hide());
  }

  /**
   * @param {string} id Id.
   * @param {string} locality Locality
   * @param {string} strInfo Info
   */
  update(id, locality, strInfo) {
    // day1
    const info = strInfo.split('|');
    let date = info[1].split('-');
    this.forecastData_.localityId = id;
    this.forecastData_.locality = locality;
    this.forecastData_.day1 = {
      date: `${date[2]}/${date[1]}/${date[0]}`,
      morning: SpanIcon[`F${info[6]}`],
      afternoon: SpanIcon[`F${info[7]}`],
      tempMin: info[11],
      tempMax: info[10],
      rain: VMeteoPopup.getRainProbabilityDetail(info[6], info[7], info[2], info[3]),
      wind: VMeteoPopup.getWindIcon(info[8]),
    };

    // day2
    date = info[12].split('-');
    this.forecastData_.day2 = {
      date: `${date[2]}/${date[1]}/${date[0]}`,
      morning: SpanIcon[`F${info[17]}`],
      afternoon: SpanIcon[`F${info[18]}`],
      tempMin: info[22],
      tempMax: info[21],
      rain: VMeteoPopup.getRainProbabilityDetail(info[17], info[18], info[13], info[14]),
      wind: VMeteoPopup.getWindIcon(info[19]),
    };

    // day3
    date = info[23].split('-');
    this.forecastData_.day3 = {
      date: `${date[2]}/${date[1]}/${date[0]}`,
      fullday: SpanIcon[`F${info[28]}`],
      tempMin: info[31],
      tempMax: info[30],
      rain: VMeteoPopup.getRainProbabilitySummary(info[28], info[24]),
    };

    // day4
    date = info[32].split('-');
    this.forecastData_.day4 = {
      date: `${date[2]}/${date[1]}/${date[0]}`,
      fullday: SpanIcon[`F${info[35]}`],
      tempMin: info[37],
      tempMax: info[36],
      rain: VMeteoPopup.getRainProbabilitySummary(info[35], info[33]),
    };

    // day5
    date = info[38].split('-');
    this.forecastData_.day5 = {
      date: `${date[2]}/${date[1]}/${date[0]}`,
      fullday: SpanIcon[`F${info[41]}`],
      tempMin: info[43],
      tempMax: info[42],
      rain: VMeteoPopup.getRainProbabilitySummary(info[41], info[39]),
    };

    // day6
    date = info[44].split('-');
    this.forecastData_.day6 = {
      date: `${date[2]}/${date[1]}/${date[0]}`,
      fullday: SpanIcon[`F${info[47]}`],
      tempMin: info[49],
      tempMax: info[48],
      rain: VMeteoPopup.getRainProbabilitySummary(info[47], info[45]),
    };

    this.render_();
  }

  /**
   * @static
   * @param {string} vel Velocity
   * @return {string} Icon
   */
  static getWindIcon(vel) {
    let icon;
    const nVel = parseInt(vel, 10);
    if (nVel > 20 && nVel < 41) {
      icon = SpanIcon.V51;
    } else if (nVel > 40 && nVel < 71) {
      icon = SpanIcon.V52;
    } else if (nVel > 70) {
      icon = SpanIcon.V53;
    } else {
      icon = SpanIcon.V50;
    }
    return icon;
  }

  /**
   * @static
   * @param {string} morning Morning
   * @param {string} afternoon Afternoon
   * @param {string} prob1 Probability1
   * @param {string} prob2 Probablity2
   * @return {string|null} Probability
   */
  static getRainProbabilityDetail(morning, afternoon, prob1, prob2) {
    const nMorning = parseInt(morning, 10);
    const nAfternoon = parseInt(afternoon, 10);
    if (nMorning >= 23 && nMorning < 47) {
      return prob1;
    } if (nAfternoon >= 23 && nAfternoon < 47) {
      return prob2;
    }
    return null;
  }

  /**
   * @static
   * @param {string} fullday Full day
   * @param {string} prob Probability
   * @return {string|null} Probability summary
   */
  static getRainProbabilitySummary(fullday, prob) {
    const nFullday = parseInt(fullday, 10);
    if (nFullday >= 23 && nFullday < 47) {
      return prob;
    }
    return null;
  }
}

export default VMeteoPopup;
